import React, { useEffect } from 'react';
import { AlertCircle, Loader2, X } from 'lucide-react';
import type { DepotUploadedDocument } from '../types';

/**
 * État du contenu à afficher dans la modale.
 * - loading : fetch en cours
 * - success : blob URL prêt à être consommé par <iframe>/<img>
 * - error   : message à afficher (DDN invalidée, fichier disparu, etc.)
 */
export type DocumentPreviewState =
  | { kind: 'loading' }
  | { kind: 'success'; objectUrl: string; contentType: string }
  | { kind: 'error'; message: string };

interface Props {
  doc: DepotUploadedDocument;
  state: DocumentPreviewState;
  onClose: () => void;
}

/**
 * Modale plein écran de prévisualisation d'un document déposé.
 *
 * Volontairement simple — pas de Headless UI : on n'a pas besoin de focus
 * trap sophistiqué ici (l'utilisateur n'a qu'un bouton Fermer), juste un
 * click backdrop + Esc qui ferment.
 *
 * Le parent (DepotUploadZone) reste owner du cycle de vie du blob URL :
 * il le crée via URL.createObjectURL puis le révoque à la fermeture pour
 * éviter une fuite mémoire. On ne fait pas ça ici parce que le composant
 * peut être démonté/remonté pendant que le blob est encore utile.
 */
export const DepotDocumentPreview: React.FC<Props> = ({
  doc,
  state,
  onClose
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Aperçu ${doc.fileName}`}
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        // Stop propagation : un click DANS la carte ne ferme pas la modale,
        // seul le backdrop le fait.
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl h-[90vh] bg-white rounded-xl shadow-xl flex flex-col overflow-hidden"
      >
        <header className="flex items-center justify-between gap-4 border-b border-gray-200 px-5 py-3">
          <h2 className="text-base font-medium text-gray-800 truncate">
            {doc.fileName}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer l'aperçu"
            className="flex-shrink-0 p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-auto bg-gray-50">
          <PreviewBody state={state} doc={doc} />
        </div>
      </div>
    </div>
  );
};

const PreviewBody: React.FC<{
  state: DocumentPreviewState;
  doc: DepotUploadedDocument;
}> = ({ state, doc }) => {
  if (state.kind === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-500">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm">Chargement du document…</p>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 px-6 text-center">
        <AlertCircle className="w-10 h-10 text-red-500" />
        <p className="text-sm text-red-700">{state.message}</p>
      </div>
    );
  }

  // success — on switch sur le content-type pour choisir <img> ou <iframe>.
  // Tout type non-image part en <iframe> : le PDF s'affiche nativement dans
  // les navigateurs modernes, et un format inconnu déclencherait juste un
  // téléchargement (acceptable).
  if (state.contentType.startsWith('image/')) {
    return (
      <div className="flex items-center justify-center min-h-full p-4">
        <img
          src={state.objectUrl}
          alt={doc.fileName}
          className="max-w-full max-h-full object-contain"
        />
      </div>
    );
  }

  return (
    <iframe
      src={state.objectUrl}
      title={doc.fileName}
      className="w-full h-full border-0"
    />
  );
};
